import { Activity, CheckCircle2 } from 'lucide-react';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { getSymptomDisplayName, SEVERITY_DISPLAY } from '@/utils/medicalTerms';

/**
 * Affichage du dernier check-in du patient
 * Utilisé par NursePatientDetail et DoctorPatientDetail
 */

const SEVERITY_LABELS = { 1: "Léger", 2: "Modéré", 3: "Sévère" };

const SEVERITY_COLORS = {
  1: "bg-yellow-100 text-yellow-700 border-yellow-300",
  2: "bg-orange-100 text-orange-700 border-orange-300",
  3: "bg-red-100 text-red-700 border-red-300"
};

const LEGACY_COLORS = {
  ROUGE: "bg-red-100 text-red-700 border-red-300",
  ORANGE: "bg-orange-100 text-orange-700 border-orange-300",
  VERT: "bg-green-100 text-green-700 border-green-300"
};

export default function LastCheckinCard({ checkin }) {
  if (!checkin) {
    return (
      <div className="dash-card">
        <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <Activity className="w-5 h-5 text-emerald-600" />
          Dernier check-in
        </h3>
        <p className="text-slate-400 text-center py-8 min-h-[160px] flex items-center justify-center">Aucun check-in</p>
      </div>
    );
  }

  const hasScoredSymptoms = checkin.symptoms && Object.keys(checkin.symptoms).length > 0;
  const hasPositiveSymptoms = hasScoredSymptoms && Object.values(checkin.symptoms).some(v => Number(v) > 0);
  const noUrgentSymptoms = !checkin.symptomsUrgent || !Object.keys(checkin.symptomsUrgent).some(k => checkin.symptomsUrgent[k]);
  const noLegacyMain = !checkin.symptomsMain || Object.keys(checkin.symptomsMain).length === 0;
  const patientIsWell = (checkin.alertLevel || checkin.status || "").toUpperCase() === "VERT" && (!hasScoredSymptoms || !hasPositiveSymptoms) && noUrgentSymptoms && noLegacyMain;

  return (
    <div className="dash-card">
      <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
        <Activity className="w-5 h-5 text-emerald-600" />
        Dernier check-in
      </h3>
      <div className="space-y-4">
        {patientIsWell && (
          <div className="rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 flex items-center gap-3" data-testid="last-checkin-well-highlight">
            <CheckCircle2 className="w-6 h-6 text-emerald-700 flex-shrink-0" />
            <div>
              <p className="text-base font-bold text-emerald-900">Le patient a indiqué : “Je vais bien”</p>
              <p className="text-sm text-emerald-700">Aucun symptôme signalé sur ce check-in.</p>
            </div>
          </div>
        )}
        <div className="flex justify-between items-center">
          <span className="text-sm text-slate-500">
            {new Date(checkin.createdAt).toLocaleDateString("fr-FR", {
              weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit"
            })}
          </span>
          <span className="inline-flex items-center gap-1.5" title="Niveau calculé pour ce check-in (le statut clinique du patient est affiché dans l'en-tête)">
            <span className="text-xs text-slate-500">Niveau du check-in :</span>
            <StatusBadge status={checkin.alertLevel || checkin.status || "STABLE"} testId="last-checkin-level-badge" />
          </span>
        </div>

        {/* NEW FORMAT: Score-based symptoms (0-3) */}
        {checkin.symptoms && Object.keys(checkin.symptoms).length > 0 && (
          <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
            <p className="text-sm font-medium text-slate-700 mb-2">Effets indésirables :</p>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(checkin.symptoms).filter(([_, v]) => v > 0).map(([key, value]) => (
                <div key={key} className={`px-2 py-1.5 rounded-lg border text-xs font-medium ${SEVERITY_COLORS[value] || "bg-slate-100 text-slate-600"}`}>
                  {getSymptomDisplayName(key)}: {SEVERITY_LABELS[value] || value}
                </div>
              ))}
              {Object.entries(checkin.symptoms).filter(([_, v]) => v > 0).length === 0 && (
                <p className="text-sm font-semibold text-emerald-700 col-span-2" data-testid="last-checkin-no-symptom-label">Aucun symptôme signalé</p>
              )}
            </div>
          </div>
        )}

        {/* LEGACY: Symptômes urgents (old format) */}
        {checkin.symptomsUrgent && Object.keys(checkin.symptomsUrgent).some(k => checkin.symptomsUrgent[k]) && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm font-medium text-red-800 mb-2">Symptômes urgents signalés :</p>
            <div className="flex flex-wrap gap-2">
              {checkin.symptomsUrgent.dyspnea && <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-lg">Dyspnée</span>}
              {checkin.symptomsUrgent.chestPain && <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-lg">Douleur thoracique</span>}
              {checkin.symptomsUrgent.fever && <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-lg">Fièvre</span>}
              {checkin.symptomsUrgent.bleeding && <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-lg">Saignement</span>}
              {checkin.symptomsUrgent.confusion && <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-lg">Confusion</span>}
              {checkin.symptomsUrgent.severeVomiting && <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-lg">Vomissements sévères</span>}
              {checkin.symptomsUrgent.severeDiarrhea && <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-lg">Diarrhée sévère</span>}
              {checkin.symptomsUrgent.severePain && <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-lg">Douleur sévère</span>}
            </div>
          </div>
        )}

        {/* LEGACY: Symptômes principaux (old format) */}
        {checkin.symptomsMain && Object.keys(checkin.symptomsMain).length > 0 && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm font-medium text-amber-800 mb-2">Symptômes principaux :</p>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(checkin.symptomsMain).map(([key, value]) => (
                <div key={key} className={`px-2 py-1.5 rounded-lg border text-xs font-medium ${LEGACY_COLORS[value] || "bg-slate-100 text-slate-600"}`}>
                  {getSymptomDisplayName(key)}: {value}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Constantes vitales */}
        <div className={`grid grid-cols-2 sm:grid-cols-3 gap-3 ${checkin.imc ? "lg:grid-cols-6" : "lg:grid-cols-5"}`} data-testid="last-checkin-vitals-grid">
          <div className="p-3 bg-slate-50 rounded-lg text-center">
            <p className="text-xl font-bold text-slate-800">{checkin.vitals?.temperature || "—"}°C</p>
            <p className="text-xs text-slate-500">Température</p>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg text-center">
            <p className="text-xl font-bold text-slate-800">{checkin.vitals?.heartRate || "—"}</p>
            <p className="text-xs text-slate-500">Pouls (bpm)</p>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg text-center">
            <p className="text-xl font-bold text-slate-800">{checkin.vitals?.spo2 || checkin.vitals?.spO2 || "—"}%</p>
            <p className="text-xs text-slate-500">SpO2</p>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg text-center">
            <p className="text-xl font-bold text-slate-800">{checkin.vitals?.weight || "—"}</p>
            <p className="text-xs text-slate-500">Poids (kg)</p>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg text-center">
            <p className="text-xl font-bold text-slate-800">
              {checkin.vitals?.systole && checkin.vitals?.diastole 
                ? `${checkin.vitals.systole}/${checkin.vitals.diastole}`
                : checkin.vitals?.bloodPressure || "—"}
            </p>
            <p className="text-xs text-slate-500">Tension</p>
          </div>
          {checkin.imc && (
            <div className="p-3 bg-blue-50 rounded-lg text-center">
              <p className="text-xl font-bold text-blue-800">{checkin.imc.value}</p>
              <p className="text-xs text-blue-600">IMC ({checkin.imc.category})</p>
            </div>
          )}
        </div>

        {/* Raisons d'alerte */}
        {checkin.alertReasons && checkin.alertReasons.length > 0 && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm font-medium text-amber-800">Règles déclenchées :</p>
            <ul className="mt-1 text-sm text-amber-700">
              {checkin.alertReasons.map((r, i) => (
                <li key={i}>• {r.replace("rule:", "").trim()}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
